import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

function AddCategory() {
  const [categoryName, setCategoryName] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [status, setStatus] = useState({ type: "", text: "" }); // type: success | error | info
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const pageRef = useRef(null);
  const previewRef = useRef(null);
  const statusRef = useRef(null);

  // GSAP: page entrance
  useLayoutEffect(() => {
    if (!pageRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".ac-anim",
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.06, ease: "power2.out" }
      );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  // GSAP: preview pop-in
  useEffect(() => {
    if (!imagePreview || !previewRef.current) return;
    gsap.fromTo(
      previewRef.current,
      { opacity: 0, y: 10, scale: 0.98 },
      { opacity: 1, y: 0, scale: 1, duration: 0.28, ease: "power2.out" }
    );
  }, [imagePreview]);

  // GSAP: status slide-in
  useEffect(() => {
    if (!status.text || !statusRef.current) return;
    gsap.fromTo(
      statusRef.current,
      { opacity: 0, y: -8 },
      { opacity: 1, y: 0, duration: 0.25, ease: "power2.out" }
    );
  }, [status]);

  const setMsg = (type, text) => setStatus({ type, text });

  const validate = () => {
    if (!categoryName.trim()) {
      setMsg("error", "Category name cannot be empty.");
      return false;
    }
    if (!imageFile) {
      setMsg("error", "Please upload a category image.");
      return false;
    }
    // Optional: file size validation (2MB)
    if (imageFile && imageFile.size > 2 * 1024 * 1024) {
      setMsg("error", "Image is too large. Max size is 2MB.");
      return false;
    }
    return true;
  };

  const handleImage = (file) => {
    if (!file) return;

    // Basic file type validation
    const ok = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"].includes(file.type);
    if (!ok) {
      setMsg("error", "Unsupported file type. Please upload PNG/JPG/GIF/WEBP.");
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    setMsg("info", "Image selected. You can now add category name and submit.");
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    handleImage(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setMsg("info", "Image removed.");
  };

  // Drag & drop events
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    handleImage(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", text: "" });

    if (!validate()) return;

    setIsSubmitting(true);
    setMsg("info", "Uploading category...");

    try {
      const formData = new FormData();
      formData.append("category_name", categoryName.trim());
      formData.append("image", imageFile);

      const res = await fetch(`${ApiURl}/makeCategory.php`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data?.status === "success") {
        setMsg("success", "Category created successfully.");
        setCategoryName("");
        setImageFile(null);
        setImagePreview(null);
      } else {
        setMsg("error", data?.message || "Error adding category.");
      }
    } catch (err) {
      console.error(err);
      setMsg("error", "An error occurred while adding the category.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusStyles = (() => {
    switch (status.type) {
      case "success":
        return "border-[#cfe2d1] bg-[#edf7ee] text-[#47704e]";
      case "error":
        return "border-[#edcbc7] bg-[#fff0ee] text-[#9d3d36]";
      case "info":
        return "border-[#d4af37]/30 bg-[#fff7e6] text-[#7d5a19]";
      default:
        return "border-[#dfd1c6] bg-[#f4ece5] text-[#6d5a53]";
    }
  })();

  return (
    <div ref={pageRef} className="add-category-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      {/* Page container */}
      <div className="ac-anim mx-auto max-w-[1280px] overflow-hidden rounded-[26px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_22px_60px_rgba(43,0,0,0.08)] sm:p-6 lg:p-8">
        {/* Title */}
        <div className="ac-anim mb-7 rounded-[20px] border border-[#e3d6cb] bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.12),transparent_34%),linear-gradient(135deg,#fffaf5,#f2e6dc)] p-5 sm:p-6">
          <h2 className="font-serif text-3xl font-medium tracking-tight text-[#2B0000] sm:text-4xl">Add Category</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[#6f5e57]">
            Upload a category image, add a clear name, and create a reusable product category.
          </p>
        </div>

        {/* Status */}
        {status.text && (
          <div
            ref={statusRef}
            className={`ac-anim mb-6 rounded-2xl border px-4 py-3.5 text-sm shadow-[0_12px_30px_rgba(43,0,0,0.06)] ${statusStyles}`}
          >
            {status.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="ac-anim">
          {/* Responsive layout */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.08fr_0.92fr]">
            {/* Upload card */}
            <div className="rounded-[22px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f3e8dd)] p-4 shadow-[0_16px_42px_rgba(43,0,0,0.06)] sm:p-6">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-xl font-medium text-[#2B0000]">Category Image</h3>
                {imagePreview && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="rounded-lg border border-[#d6c4b5] bg-white px-3 py-1.5 text-xs font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="mt-3 text-sm text-[#6f5e57]">
                Supported: PNG, JPG, GIF, WEBP (Max 2MB)
              </div>

              {/* Drop zone */}
              <label
                className={[
                  "mt-4 block cursor-pointer rounded-[20px] border-2 border-dashed p-6 transition sm:p-8",
                  isDragOver ? "border-[#c59a4a] bg-[#fff4d9]" : "border-[#d4af37]/35 bg-[#fbf3eb] hover:border-[#c59a4a] hover:bg-[#fff7e8]",
                ].join(" ")}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
              >
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileInput}
                  accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
                />

                <div className="flex flex-col items-center justify-center text-center">
                  <div className="mb-3 rounded-full border border-[#d4af37]/30 bg-white px-3 py-1 text-xs font-semibold text-[#6f5523] shadow-sm">
                    Drag & Drop
                  </div>
                  <div className="text-sm text-[#4f3d36]">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </div>
                  <div className="mt-1 text-xs text-[#7d6c64]">Your image preview will appear below.</div>
                </div>
              </label>

              {/* Preview */}
              {imagePreview && (
                <div ref={previewRef} className="mt-4">
                  <div className="text-sm font-semibold text-[#4a352f]">Preview</div>
                  <div className="mt-3 overflow-hidden rounded-[20px] border border-[#d8c6b7] bg-white shadow-[0_14px_36px_rgba(43,0,0,0.07)]">
                    <img
                      src={imagePreview}
                      alt="Selected category"
                      className="h-60 w-full bg-[#fffaf5] object-contain sm:h-72"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Form card */}
            <div className="rounded-[22px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f3e8dd)] p-4 shadow-[0_16px_42px_rgba(43,0,0,0.06)] sm:p-6">
              <div className="mb-5"><p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#9a7740]">Category Information</p><h3 className="mt-1 font-serif text-xl font-medium text-[#2B0000]">Category Details</h3></div>

              <div className="mt-4">
                <label className="text-sm font-semibold text-[#4a352f]">Category Name</label>
                <input
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  type="text"
                  placeholder="e.g. Sarees"
                  className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
                <div className="mt-1 text-xs text-[#7d6c64]">
                  Use a short name customers understand.
                </div>
              </div>

              {/* Submit */}
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={[
                    "inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold transition shadow-[0_12px_28px_rgba(43,0,0,0.16)]",
                    isSubmitting ? "cursor-not-allowed bg-[#b7a69b] text-white" : "bg-[#2B0000] text-[#f4dfae] hover:-translate-y-0.5 hover:bg-[#4A090B] active:translate-y-[1px]",
                  ].join(" ")}
                >
                  {isSubmitting ? "Uploading..." : "Add Category"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setCategoryName("");
                    setImageFile(null);
                    setImagePreview(null);
                    setStatus({ type: "", text: "" });
                  }}
                  className="rounded-xl border border-[#d6c4b5] bg-white px-5 py-3 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
                >
                  Reset
                </button>
              </div>

              {/* Helper */}
              <div className="mt-5 rounded-xl border border-[#e4d8cd] bg-white/70 p-3 text-xs leading-5 text-[#7d6c64]">
                Use a clean, high-quality image with the category centered for the best storefront result.
              </div>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .add-category-luxury {
          background:
            radial-gradient(circle at 7% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .add-category-luxury input,
        .add-category-luxury button {
          font: inherit;
        }

        .add-category-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .add-category-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.5) rgba(212,175,55,.07);
        }

        .add-category-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .add-category-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .add-category-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.5);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .add-category-luxury {
            padding: 10px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .add-category-luxury *,
          .add-category-luxury *::before,
          .add-category-luxury *::after {
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}

export default AddCategory;